import React, { useMemo, useState, ReactNode, useEffect } from "react";
import classNames from "classnames";
import { eventCenter } from "@tarojs/taro";

import Radio from "..";

export type RadioItemType = {
  label: string | ReactNode;
  value: string;
  disabled?: boolean;
  checked?: boolean;
};
export type RadioItem = RadioItemType | string;
export type RadioGroupProps = {
  option: RadioItem[];
  onChange?: (value: string | number) => void;
  name: string;
  circle?: boolean;
  align?: "center" | "end";
  direction?: "rtl";
  justify?: "around" | "between";
  id?: string;
  padding?: true;
  border?: boolean;
  style?: React.CSSProperties;
  radioStyle?: React.CSSProperties;
  defaultChecked?: string | number;
  color?: string;
  inline?: boolean;
  size?: number;
  fancy?: boolean;
  checkedClass?: string;
  dataTrackId?: string;
};

const RadioGroup: React.FC<RadioGroupProps> = props => {
  const {
    option,
    onChange,
    name,
    padding,
    border,
    radioStyle,
    defaultChecked,
    inline,
    style,
    checkedClass = "",
    dataTrackId = "RadioGroup",
    ...rest
  } = props;

  let getOpions = useMemo(() => {
    return option?.map(item => {
      if (typeof item === "string") {
        return {
          label: item,
          value: item,
          checked: defaultChecked === item,
        };
      } else {
        if (defaultChecked === item.value) {
          item.checked = true;
        }
        return item;
      }
    });
  }, [option, defaultChecked]);

  const [innerOptions, setInnerOptions] = useState(getOpions);

  useEffect(() => {
    setInnerOptions(getOpions);
  }, [getOpions]);

  const handleChange = idx => {
    let newoption = [...innerOptions];
    let newValue = "";
    for (let index = 0; index < newoption.length; index++) {
      if (index === idx) {
        newoption[index].checked = true;
        newValue = newoption[index].value;
      } else {
        newoption[index].checked = false;
      }
    }
    setInnerOptions(newoption);
    onChange?.(newValue);
    if (process.env.TARO_ENV !== "h5") {
      eventCenter.trigger("radioChange", idx);
    }
  };

  const cls = classNames("xh-radio-group", { padding }, { border }, { inline });

  return (
    <div className={cls} style={style}>
      {getOpions.map((item, index) => {
        return (
          <Radio
            dataTrackId={`${dataTrackId}_option_${index}`}
            label={item.label}
            value={item.value}
            disabled={item.disabled}
            name={name}
            key={index}
            className={classNames("xh-radio-group-list", {
              [checkedClass]: item.checked,
            })}
            circle
            onChange={() => handleChange(index)}
            checked={item.checked}
            style={radioStyle}
            index={index}
            group
            {...rest}
          />
        );
      })}
    </div>
  );
};

export default RadioGroup;
